import { and, not } from '@ember/object/computed';
import EmberObject from '@ember/object';
import { computed, action } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { htmlSafe } from '@ember/template';
import { MAX_SAMPLES_COUNT } from 'cio/models/import';
import { RESTRICTED_ATTRIBUTES, PROTECTED_ATTRIBUTES, REQUIRED_EVENT_IMPORT_ATTRIBUTES } from 'cio/utils/attributes';
import { IdentifierKey } from '../identifiers';

export interface SampleType {
  name: string;
  customer_id: string;
  data: string;
  timestamp: string;
}

export const filterSamples = (samples: string[][], index: number) => {
  return samples.map((row) => row[index]).slice(0, MAX_SAMPLES_COUNT);
};

export const filterEventSamples = (samples: SampleType[], attribute: string) => {
  return samples
    .map((row) => {
      if (attribute === REQUIRED_EVENT_IMPORT_ATTRIBUTES.name) {
        return row.name;
      } else if (attribute === REQUIRED_EVENT_IMPORT_ATTRIBUTES.customer_id) {
        return row.customer_id;
      } else if (attribute === REQUIRED_EVENT_IMPORT_ATTRIBUTES.timestamp) {
        return row?.timestamp;
      } else {
        return JSON.parse(row.data)[attribute];
      }
    })
    .slice(0, MAX_SAMPLES_COUNT);
};

export class ImportAttribute extends EmberObject {
  key!: string;
  to!: string;
  samples!: string[][] | SampleType[];
  originalKey!: string;
  originalTo!: string;

  skip = false;
  originalSkip = false;
  restricted = false;
  noId = false;

  initialWarnings?: string[];
  otherMappings?: string[];
  sentAttributes?: string[];

  primaryAttribute = 'id';

  init() {
    super.init();

    this.set('originalTo', this.to);
    this.set('originalSkip', this.skip);
    this.set('originalKey', this.key);

    this.set('initialWarnings', this.initialWarnings ?? []);

    if (isNone(this.to)) {
      // Let's try to predict which key they would like to map to. In order:
      // - an exact, case-sensitive, match to an existing sent attribute
      // - a case-insensitive match to an identifier key
      // - a case-insensitive match to an existing sent attribute
      // - a permutation of an identifier, like userId or email_address
      let specialCaseAttributes: Record<string, string> = {
        user_id: IdentifierKey.Id,
        userid: IdentifierKey.Id,
        euid: IdentifierKey.Id,
        email_address: IdentifierKey.Email,
      };
      let exactSentAttributeMatch = this.sentAttributes?.find((maybeAttr) => maybeAttr === this.key);
      let existingAttribute = [...RESTRICTED_ATTRIBUTES, ...(this.sentAttributes ?? [])].find(
        (maybeAttr) => maybeAttr.toLowerCase() === this.key.toLowerCase(),
      );
      let specialCaseAttribute = specialCaseAttributes[this.key.toLowerCase().replace(' ', '_')];

      this.setProperties({
        to: exactSentAttributeMatch ?? existingAttribute ?? specialCaseAttribute ?? this.key,
        originalTo: existingAttribute ?? specialCaseAttribute ?? this.key,
      });
    }
  }

  @computed('to', 'primaryAttribute')
  get isRequired() {
    return this.to === this.primaryAttribute;
  }
  @and('isNotMappingToRestrictedAttribute', 'isNotMatchEmailMappingToId') isValid!: boolean;

  @computed('to', 'isDirty')
  get isMappingToRestrictedAttribute() {
    return this.isDirty && this.isRestricted(this.to);
  }
  @not('isMappingToRestrictedAttribute') isNotMappingToRestrictedAttribute!: boolean;

  @computed('to', 'isDirty', 'primaryAttribute')
  get isMatchEmailMappingToId() {
    return this.primaryAttribute === 'email' && this.isDirty && this.to === 'id';
  }
  @not('isMatchEmailMappingToId') isNotMatchEmailMappingToId!: boolean;

  @computed('key', 'to', 'isDirty')
  get hidden() {
    let { key, to, isDirty } = this;

    if (isDirty) {
      return false;
    }

    return this.isProtected(key) || this.isRestricted(key) || (!isNone(to) && this.isRestricted(to));
  }

  @computed('originalKey', 'key', 'originalTo', 'to', 'originalSkip', 'skip', 'restricted')
  get isDirty() {
    let { originalKey, key, originalTo, to, originalSkip, skip, restricted } = this;

    if (originalSkip !== skip) {
      return true;
    }

    // In case of a restricted attribute, to key changes, not the mapping.
    if (restricted) {
      return originalKey !== key;
    } else {
      if (!isNone(originalTo)) {
        return originalTo !== to;
      }

      return key !== to;
    }
  }

  @computed('initialWarnings.[]', 'otherMappings.[]', 'to', 'noId')
  get warnings() {
    let { to, initialWarnings, otherMappings } = this;

    // The record here is to approximate the SafeString type, which is private and likely to change
    let warnings: (Record<string, any> | string)[] = [...(initialWarnings ?? [])];

    if (isEmpty(to)) {
      warnings.push("Attribute names can't be empty");
    }

    if (to.indexOf(' ') > -1) {
      warnings.push(
        htmlSafe(
          `It's best practice not to use whitespace in attribute names. <a href="https://customer.io/docs/getting-started-integration-planning#tips-and-best-practices-for-profile-attributes" rel="noopener noreferrer" target="_blank">Read more</a>.`,
        ),
      );
    }

    if (otherMappings?.includes(to)) {
      warnings.push('You are mapping multiple columns to this attribute');
    }

    if (this.to === 'id' && this.noId) {
      warnings.push("You can't map to id in this workspace. Consider user_id instead.");
    }

    return warnings;
  }

  @computed('noId', 'to')
  get hints() {
    // The record here is to approximate the SafeString type, which is private and likely to change
    let hints: (Record<string, any> | string)[] = [];
    let compareAttributes: Record<string, string> = {
      id: 'id',
      created_at: 'created_at',
      unsubscribed: 'unsubscribed',
      email: 'email',
      email_address: 'email',
      'email address': 'email',
      user_id: 'id',
      customer_id: 'id',
    };
    let compareAttributesKeys = Object.keys(compareAttributes);
    let similarToRestrictedAttribute = compareAttributesKeys.find((attr) => attr === this.to?.toLowerCase());
    let exactRestrictedAttributeMatch = compareAttributes[similarToRestrictedAttribute ?? ''] === this.to;

    if (!exactRestrictedAttributeMatch && similarToRestrictedAttribute) {
      if (similarToRestrictedAttribute === 'id' && this.noId) {
        hints.push(
          htmlSafe(
            `Heads up—this column is not mapping to the <a href="https://customer.io/docs/uploading-people#reserved-attributes" target="_blank" rel="noopener noreferrer">reserved attribute</a> ${
              compareAttributes[this.to.toLowerCase()]
            } because attribute names are case sensitive.`,
          ),
        );
      } else {
        hints.push(
          htmlSafe(
            `Did you mean to map to the <a href="https://customer.io/docs/uploading-people#reserved-attributes" target="_blank" rel="noopener noreferrer">reserved attribute</a> ${
              compareAttributes[this.to.toLowerCase()]
            }? Attribute names are case sensitive.`,
          ),
        );
      }
    }

    return hints;
  }

  @action
  isRestricted(key: string) {
    return RESTRICTED_ATTRIBUTES.includes(key);
  }

  @action
  isProtected(key: string) {
    return PROTECTED_ATTRIBUTES.includes(key);
  }

  @action
  commit() {
    this.setProperties({
      originalKey: this.key,
      originalSkip: this.skip,
      originalTo: this.to,
    });
  }
}
